import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  Subscription,
  catchError,
  concatMap,
  from,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import { User } from '../classes/models';
import { TermsAndConditionsModalComponent } from '../modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';

@Injectable()
export class TermsAndConditionsInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private UserService: UserService
  ) {}

  termsAndConditionsModal?: NgbModalRef;
  SaveUserServiceObservable?: Observable<User>;
  SaveUserServiceReplaySubject?: ReplaySubject<User>;

  checkIfTermsAndConditionsAccepted = (
    request: HttpRequest<unknown>,
    next: HttpHandler
  ) => {
    try {
      const User = this.AuthService.getUser();

      const { id, VendorId } = User;

      const isTermsAndConditionsConfirmed =
        User.isTermsAndConditionsConfirmed ||
        Number(localStorage.getItem('isTermsAndConditionsConfirmed'));

      if (
        VendorId &&
        !isTermsAndConditionsConfirmed &&
        !this.AuthService.isImpersonating()
      ) {
        this.termsAndConditionsModal =
          this.termsAndConditionsModal ||
          this.ModalService.open(TermsAndConditionsModalComponent, {
            backdrop: 'static',
            size: 'lg'
          });

        const { result } = this.termsAndConditionsModal;

        return from(result).pipe(
          catchError(({ isTermsAndConditionsConfirmed }) => {
            this.termsAndConditionsModal = undefined;
            localStorage.removeItem('isTermsAndConditionsConfirmed');
            return throwError(
              () => new Error('Did not accept terms and conditions.')
            );
          }),
          take(1),
          concatMap(({ isTermsAndConditionsConfirmed }) => {
            this.termsAndConditionsModal = undefined;
            if (isTermsAndConditionsConfirmed) {
              localStorage.setItem('isTermsAndConditionsConfirmed', '1');
              if (this.SaveUserServiceReplaySubject) {
                return this.SaveUserServiceReplaySubject;
              }

              this.SaveUserServiceReplaySubject = new ReplaySubject(1);
              return this.UserService.saveUser({
                id,
                VendorId,
                isTermsAndConditionsConfirmed: true
              }).pipe(
                take(1),
                concatMap((response) => {
                  this.SaveUserServiceReplaySubject!.next(response);
                  this.SaveUserServiceReplaySubject!.complete();

                  // After 15 seconds, remove the replay subject.
                  setTimeout(() => {
                    this.SaveUserServiceReplaySubject = undefined;
                  }, 15000);
                  return of({});
                })
              );
            }
            return of({});
          }),
          concatMap(() => {
            return next.handle(request);
          })
        );
      } else {
        return next.handle(request);
      }
    } catch (error) {
      // Can modify this, potentially. May simply throw error.
      return next.handle(request);
    }
  };

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const nextHttpEventObservable = this.checkIfTermsAndConditionsAccepted(
      request,
      next
    );

    return nextHttpEventObservable.pipe(
      catchError((errorResponse) => {
        const { error } = errorResponse;

        if (error && error.name === 'TermsAndConditionsError') {
          localStorage.removeItem('isTermsAndConditionsConfirmed');
          return this.checkIfTermsAndConditionsAccepted(request, next);
        }

        return throwError(() => errorResponse as HttpEvent<unknown>);
      })
    );
  }
}
